// Clientes (contratantes do frete), com o prazo para receber o saldo.
import { cargasEClientes, clientes, situacoesDeEnvio } from '../dados.js';
import { linhasDoPlacar, NOTAS_DO_PAGADOR, placarDosPagamentos } from '../lib/receber.js';
import { emblemaEnvio } from '../ui/sinal.js';
import { h, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { documento as formatarDocumento, lerInteiro, plural } from '../lib/formato.js';
import { documentoValido, normalizarDocumento, tipoDoDocumento, UFS } from '../lib/validacao.js';
import { nestaTela } from '../rotas.js';
import { contaGravavel, hojeDaEmpresa, podeGravar } from '../sessao.js';
import { casca } from '../ui/casca.js';
import {
  avisar, campo, carregando, cartaoLink, confirmar, emblema, faixa, formulario, secao, vazio,
} from '../ui/componentes.js';
import { faixaDaConta } from './inicio.js';

function semAcento(texto) {
  return String(texto ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

// ---------------------------------------------------------------------
// Lista
// ---------------------------------------------------------------------
export function telaClientes() {
  const corpo = h('div', { class: 'secao' }, carregando());

  (async () => {
    const [lista, envio, { fretes }] = await Promise.all([clientes.listar(), situacoesDeEnvio('clientes'), cargasEClientes()]);
    // Placar de quem paga em dia (0.17.0)
    const placar = new Map(placarDosPagamentos(fretes, lista, hojeDaEmpresa()).map((p) => [p.id, p]));
    const gravavel = podeGravar();

    if (lista.length === 0) {
      trocar(corpo, vazio({
        simbolo: 'clientes',
        titulo: 'Nenhum cliente ainda',
        texto: 'Cadastre as empresas que contratam seus fretes. O prazo de cada uma ajuda a cobrar os saldos em atraso.',
        acao: gravavel && h('a', { class: 'botao', href: '#/clientes/novo' }, 'Cadastrar cliente'),
      }));
      return;
    }

    const cartao = (c) => cartaoLink({
      href: `#/clientes/${c.id}`,
      simbolo: 'clientes',
      titulo: c.nome,
      linhas: [
        [c.documento && formatarDocumento(c.documento), [c.cidade, c.uf].filter(Boolean).join('/')].filter(Boolean).join(' · '),
        `Saldo em ${plural(c.prazo_saldo_dias, 'dia')} após a descarga`,
        placar.get(c.id)?.pagos > 0 && `Paga em ${plural(placar.get(c.id).mediaDias, 'dia')}, em média`,
      ],
      emblemas: [
        !c.ativo && emblema('Desativado'),
        placar.has(c.id) && placar.get(c.id).nota !== 'poucos' && emblema(NOTAS_DO_PAGADOR[placar.get(c.id).nota].texto, NOTAS_DO_PAGADOR[placar.get(c.id).nota].tom),
        emblemaEnvio(envio.get(c.id))],
    });

    const resultado = h('div', { class: 'lista' });
    const mostrar = (filtro) => {
      const f = semAcento(filtro);
      const achados = lista.filter((c) => !f || semAcento(`${c.nome} ${c.documento ?? ''} ${c.cidade ?? ''}`).includes(f));
      const ativos = achados.filter((c) => c.ativo);
      const inativos = achados.filter((c) => !c.ativo);
      trocar(resultado, 
        ativos.map(cartao),
        inativos.length > 0 && h('h3', { class: 'pequeno' }, 'Desativados'),
        inativos.map(cartao),
        achados.length === 0 && h('p', { class: 'pequeno' }, 'Nenhum cliente encontrado.'),
      );
    };
    mostrar('');

    trocar(corpo, 
      gravavel && h('a', { class: 'botao', href: '#/clientes/novo' }, '+ Cliente'),
      lista.length > 5 && campo({
        rotulo: 'Procurar',
        nome: 'busca',
        tipo: 'search',
        atributos: { placeholder: 'Nome, CNPJ ou cidade', autocomplete: 'off' },
        aoMudar: (e) => mostrar(e.target.value),
      }),
      secao('Clientes', resultado, { contador: String(lista.filter((c) => c.ativo).length) }),
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({ titulo: 'Clientes', conteudo: [faixaDaConta(), corpo] });
}

// ---------------------------------------------------------------------
// Cadastro e edição
// ---------------------------------------------------------------------
export function telaCliente({ params }) {
  const tela = nestaTela();
  const novo = !params.id;
  const corpo = h('div', { class: 'secao' }, carregando());
  const titulo = h('span', null, novo ? 'Novo cliente' : 'Cliente');

  (async () => {
    const c = novo ? null : await clientes.obter(params.id);
    if (!novo && !c) {
      trocar(corpo, vazio({ simbolo: 'clientes', titulo: 'Cliente não encontrado', acao: h('a', { class: 'botao', href: '#/clientes' }, 'Ver clientes') }));
      return;
    }
    if (c) titulo.textContent = c.nome;
    const gravavel = podeGravar();

    const form = formulario({
      filhos: [
        campo({ rotulo: 'Nome ou razão social', nome: 'nome', obrigatorio: true, valor: c?.nome ?? '', atributos: { autocomplete: 'off' } }),
        campo({ rotulo: 'CNPJ ou CPF', nome: 'documento', valor: c?.documento ? formatarDocumento(c.documento) : '', formato: 'documento', dica: 'Opcional, mas ajuda nos relatórios.', atributos: { autocomplete: 'off', autocapitalize: 'characters', spellcheck: 'false' } }),
        h('div', { class: 'grade-uf' },
          campo({ rotulo: 'Cidade', nome: 'cidade', valor: c?.cidade ?? '', formato: 'nome' }),
          campo({ rotulo: 'UF', nome: 'uf', tipo: 'select', valor: c?.uf ?? '', opcoes: [['', '—'], ...UFS.map((u) => [u, u])] })),
        campo({
          rotulo: 'Prazo para pagar o saldo (dias)',
          nome: 'prazo_saldo_dias',
          obrigatorio: true,
          formato: 'dias',
          valor: c?.prazo_saldo_dias ?? 30,
          dica: 'Dias depois da descarga. Passou disso, o saldo aparece como atrasado.',
          atributos: { inputmode: 'numeric', maxlength: '3' },
        }),
        campo({ rotulo: 'Observação', nome: 'observacao', tipo: 'textarea', valor: c?.observacao ?? '', dica: 'Contato, exigências para o comprovante, etc.' }),
      ],
      textoBotao: novo ? 'Cadastrar' : 'Salvar alterações',
      somenteLeitura: !gravavel,
      validar: (d) => {
        const erros = {};
        if (d.nome.length < 2) erros.nome = 'Informe o nome.';
        if (d.documento) {
          const tipo = tipoDoDocumento(d.documento);
          if (!tipo) erros.documento = 'Digite o CPF (11 números) ou o CNPJ (14 caracteres).';
          else if (!documentoValido(d.documento, tipo)) erros.documento = `${tipo === 'PF' ? 'CPF' : 'CNPJ'} inválido. Confira o que foi digitado.`;
        }
        const prazo = lerInteiro(d.prazo_saldo_dias);
        if (prazo === null || !(prazo >= 0 && prazo <= 365)) erros.prazo_saldo_dias = 'Informe de 0 a 365 dias.';
        return erros;
      },
      aoEnviar: async (d) => {
        const dados = {
          nome: d.nome,
          documento: d.documento ? normalizarDocumento(d.documento) : null,
          cidade: d.cidade || null,
          uf: d.uf || null,
          prazo_saldo_dias: lerInteiro(d.prazo_saldo_dias),
          observacao: d.observacao || null,
        };
        if (novo) {
          await clientes.criar(dados);
          avisar('Cliente cadastrado.');
          tela.ir('/clientes', { substituir: true });
        } else {
          await clientes.atualizar(c.id, dados, c);
          avisar('Alterações salvas.');
          tela.ir('/clientes');
        }
      },
    });

    // Como ele paga (placar de quem paga em dia, 0.17.0)
    let pagamentos = null;
    if (c) {
      const { fretes, clientes: todos } = await cargasEClientes();
      const p = placarDosPagamentos(fretes.filter((f) => f.cliente_id === c.id), todos, hojeDaEmpresa())[0];
      if (p) {
        const nota = NOTAS_DO_PAGADOR[p.nota];
        pagamentos = secao('Como ele paga', h('div', { class: 'cartao' }, h('span', { class: 'cartao-corpo' },
          linhasDoPlacar(p).map((l) => h('span', { class: 'cartao-linha' }, l)),
          h('span', { class: 'cartao-emblemas' }, emblema(nota.texto, nota.tom)))));
      }
    }

    trocar(corpo, 
      c && !c.ativo && faixa('Este cliente está desativado.', 'alerta'),
      pagamentos,
      form,
      c && gravavel && blocoDesativar(c),
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({ titulo, voltar: '/clientes', conteudo: [contaGravavel() ? null : faixaDaConta(), corpo] });
}

function blocoDesativar(c) {
  const tela = nestaTela();
  const alternar = async () => {
    const ok = await confirmar({
      titulo: c.ativo ? 'Desativar este cliente?' : 'Reativar este cliente?',
      texto: c.ativo ? 'Ele sai da lista de novas cargas. O histórico continua guardado.' : 'Ele volta para a lista de cargas.',
      sim: c.ativo ? 'Desativar' : 'Reativar',
    });
    if (!ok) return;
    try {
      await clientes.atualizar(c.id, { ativo: !c.ativo });
      avisar(c.ativo ? 'Cliente desativado.' : 'Cliente reativado.');
      tela.renderizar();
    } catch (erro) {
      avisar(mensagemDeErro(erro), 'erro');
    }
  };
  const excluir = async () => {
    const ok = await confirmar({ titulo: 'Excluir este cadastro?', texto: 'Use só para cadastros feitos por engano. O registro vai para a lixeira.', sim: 'Excluir', perigo: true });
    if (!ok) return;
    try {
      await clientes.excluir(c.id);
      avisar('Cadastro excluído.');
      tela.ir('/clientes', { substituir: true });
    } catch (erro) {
      avisar(mensagemDeErro(erro), 'erro');
    }
  };
  return h('div', { class: 'acoes' },
    h('button', { class: 'botao cheio secundario', type: 'button', onClick: alternar }, c.ativo ? 'Desativar cliente' : 'Reativar cliente'),
    h('button', { class: 'botao texto', type: 'button', onClick: excluir }, 'Excluir cadastro feito por engano'));
}
